import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { ChevronLeft, ChevronRight, Download } from "lucide-react";
import { toast } from "sonner";

function PageHeader({ overline, title, action }) {
  return (
    <div className="border-b border-navy/10 px-8 py-8 bg-cream flex items-end justify-between">
      <div>
        <div className="overline text-gold-deep">{overline}</div>
        <h1 className="font-serif text-3xl md:text-4xl text-navy mt-2">{title}</h1>
        <div className="gold-rule mt-4" />
      </div>
      {action}
    </div>
  );
}

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function startOfMonth(year, month) {
  const d = new Date(year, month, 1);
  // Get Monday as start of week
  const weekday = (d.getDay() + 6) % 7; // 0..6, Mon=0
  d.setDate(d.getDate() - weekday);
  return d;
}

function isoDate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export default function Calendar() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [tasks, setTasks] = useState([]);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get("/tasks").then((r) => setTasks(r.data)).catch(() => {});
  }, []);

  const days = useMemo(() => {
    const start = startOfMonth(year, month);
    const arr = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      arr.push(d);
    }
    return arr;
  }, [year, month]);

  const tasksByDate = useMemo(() => {
    const map = {};
    for (const t of tasks) {
      if (!t.due_date) continue;
      (map[t.due_date] ||= []).push(t);
    }
    return map;
  }, [tasks]);

  const monthLabel = new Date(year, month, 1).toLocaleString("en-US", { month: "long", year: "numeric" });

  const prev = () => {
    const d = new Date(year, month - 1, 1);
    setYear(d.getFullYear()); setMonth(d.getMonth());
  };
  const next = () => {
    const d = new Date(year, month + 1, 1);
    setYear(d.getFullYear()); setMonth(d.getMonth());
  };
  const today = () => { setYear(now.getFullYear()); setMonth(now.getMonth()); };

  const downloadIcs = async () => {
    try {
      const res = await api.get("/tasks/export.ics", { responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/calendar" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "joseph-poche-tasks.ics";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Calendar file downloaded");
    } catch {
      toast.error("Could not export calendar");
    }
  };

  const todayIso = isoDate(new Date());

  return (
    <div data-testid="calendar-page">
      <PageHeader
        overline="Schedule"
        title="Calendar"
        action={
          <button onClick={downloadIcs} data-testid="calendar-export-ics" className="bg-navy text-gold px-4 py-2.5 text-xs uppercase tracking-widest hover:bg-navy-600 flex items-center gap-2">
            <Download className="h-3.5 w-3.5" /> Export .ics
          </button>
        }
      />
      <div className="p-8">
        <div className="bg-white border border-navy/10">
          <div className="flex items-center justify-between px-5 py-4 border-b border-navy/10">
            <div className="flex items-center gap-3">
              <button onClick={prev} data-testid="cal-prev" className="p-2 hover:bg-cream"><ChevronLeft className="h-4 w-4" /></button>
              <button onClick={today} data-testid="cal-today" className="text-xs uppercase tracking-widest text-navy/70 hover:text-gold px-3 py-1.5 border border-navy/15">Today</button>
              <button onClick={next} data-testid="cal-next" className="p-2 hover:bg-cream"><ChevronRight className="h-4 w-4" /></button>
            </div>
            <div className="font-serif text-xl text-navy" data-testid="cal-month-label">{monthLabel}</div>
            <div className="text-xs text-navy/40">{tasks.filter((t) => t.due_date).length} scheduled</div>
          </div>

          <div className="grid grid-cols-7 border-b border-navy/10">
            {WEEKDAYS.map((d) => (
              <div key={d} className="px-3 py-2 overline text-navy/60 border-r border-navy/5 last:border-r-0">{d}</div>
            ))}
          </div>

          <div className="grid grid-cols-7">
            {days.map((d, i) => {
              const iso = isoDate(d);
              const inMonth = d.getMonth() === month;
              const isToday = iso === todayIso;
              const dayTasks = tasksByDate[iso] || [];
              return (
                <button
                  key={i}
                  onClick={() => setSelected({ date: iso, tasks: dayTasks })}
                  data-testid={`cal-day-${iso}`}
                  className={`min-h-[110px] text-left border-r border-b border-navy/5 last:border-r-0 p-2 ${
                    inMonth ? "bg-white" : "bg-cream/40"
                  } hover:bg-cream transition-colors`}
                >
                  <div className={`text-xs ${isToday ? "text-gold-deep font-semibold" : inMonth ? "text-navy" : "text-navy/30"}`}>
                    {d.getDate()}
                    {isToday && <span className="ml-1 text-[9px] uppercase tracking-widest">Today</span>}
                  </div>
                  <div className="mt-2 space-y-1">
                    {dayTasks.slice(0, 3).map((t) => (
                      <div
                        key={t.id}
                        className={`text-[10px] px-1.5 py-0.5 truncate ${
                          t.status === "Done"
                            ? "bg-navy/5 text-navy/40 line-through"
                            : t.priority === "High"
                            ? "bg-destructive/10 text-destructive"
                            : t.priority === "Medium"
                            ? "bg-gold/15 text-gold-deep"
                            : "bg-navy/5 text-navy/70"
                        }`}
                      >
                        {t.title}
                      </div>
                    ))}
                    {dayTasks.length > 3 && (
                      <div className="text-[10px] text-navy/40">+{dayTasks.length - 3} more</div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 bg-navy/40 flex items-center justify-center p-4" data-testid="cal-day-modal">
          <div className="bg-white max-w-lg w-full p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="overline text-gold-deep">{selected.date}</div>
                <h3 className="font-serif text-2xl text-navy">Tasks</h3>
              </div>
              <button onClick={() => setSelected(null)} className="text-navy/40 hover:text-navy">×</button>
            </div>
            <div className="gold-rule mb-4" />
            {selected.tasks.length === 0 && <div className="text-sm italic text-navy/40">No tasks on this day.</div>}
            <ul className="space-y-2">
              {selected.tasks.map((t) => (
                <li key={t.id} className="border-l-2 border-gold pl-3 py-1">
                  <div className={`text-sm ${t.status === "Done" ? "line-through text-navy/40" : "text-navy"}`}>{t.title}</div>
                  <div className="text-xs text-navy/50">{t.priority} · {t.status}</div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
